import {
  useState,
} from "react";

import {
  useQuery,
} from "@tanstack/react-query";

import {
  useNavigate,
} from "react-router-dom";

import {
  api,
} from "@/lib/api";

import {
  useFarm,
} from "@/context/FarmContext";

import {
  getCurrencySymbol,
} from "@/lib/currency";

import {
  PageHeader,
  NoFarm,
} from "@/components/Page";

import {
  Card,
} from "@/components/ui/card";

import {
  Input,
} from "@/components/ui/input";

import {
  Label,
} from "@/components/ui/label";

import {
  Button,
} from "@/components/ui/button";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

import {
  ArrowRight,
  Beef,
  BookOpen,
  ChevronDown,
  CircleDollarSign,
  Euro,
  FlaskConical,
  Lightbulb,
  PackageCheck,
  Send,
  Sparkles,
  TrendingUp,
  Users,
  Wheat,
  Zap,
} from "lucide-react";


const PILLAR_ICON = {
  energy:
    Zap,
  protein:
    Beef,
  fibre:
    Wheat,
  minerals:
    FlaskConical,
  feed_security:
    PackageCheck,
  economics:
    CircleDollarSign,
};


const PRIO = {
  critical: {
    label:
      "Critical",

    chip:
      "bg-destructive text-white",

    border:
      "border-l-destructive",
  },

  high: {
    label:
      "High",

    chip:
      "bg-[hsl(24,88%,48%)] text-white",

    border:
      "border-l-[hsl(24,88%,48%)]",
  },

  medium: {
    label:
      "Medium",

    chip:
      "bg-[hsl(43,74%,52%)] text-[hsl(38,70%,20%)]",

    border:
      "border-l-[hsl(43,74%,52%)]",
  },

  low: {
    label:
      "Low",

    chip:
      "bg-secondary text-muted-foreground",

    border:
      "border-l-muted-foreground",
  },
};


const QUICK_PROMPTS = [
  "Fix my milking-cow ration",
  "Reduce feed cost",
  "Check winter feed security",
  "Why is my mineral score low?",
];


function scoreColor(
  score
) {
  if (
    score == null
  ) {
    return "hsl(0,0%,72%)";
  }

  if (
    score >= 85
  ) {
    return "hsl(145,47%,35%)";
  }

  if (
    score >= 55
  ) {
    return "hsl(43,74%,45%)";
  }

  return "hsl(0,61%,46%)";
}


export function ScoreRing({
  score,
  size = 72,
}) {
  const pct =
    score ??
    0;

  const colour =
    scoreColor(
      score
    );

  return (
    <div
      className="grid shrink-0 place-items-center rounded-full"
      style={{
        width:
          size,

        height:
          size,

        background:
          `conic-gradient(${colour} ${pct * 3.6}deg, hsl(140,20%,90%) 0deg)`,
      }}
    >

      <div
        className="grid place-items-center rounded-full bg-white"
        style={{
          width:
            size - 12,

          height:
            size - 12,
        }}
      >

        <span
          className="font-display font-extrabold tabular-nums text-[hsl(148,38%,16%)]"
          style={{
            fontSize:
              size / 4,
          }}
        >
          {score ??
            "—"}
        </span>

      </div>

    </div>
  );
}


function PillarTile({
  pillar,
  active,
  onClick,
}) {
  const Icon =
    PILLAR_ICON[
      pillar.key
    ] ||
    Sparkles;

  return (
    <button
      data-testid={`pillar-tile-${pillar.key}`}
      onClick={
        onClick
      }
      className={`rounded-xl border p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-md ${
        active
          ? "border-primary bg-primary/[0.03] ring-2 ring-primary/30"
          : "border-border"
      }`}
    >

      <div className="mb-3 flex items-center justify-between">

        <div className="grid h-8 w-8 place-items-center rounded-full bg-primary/10">
          <Icon className="h-4 w-4 text-primary" />
        </div>

        <span
          className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
          style={{
            background:
              `${scoreColor(
                pillar.score
              )}20`,

            color:
              scoreColor(
                pillar.score
              ),
          }}
        >
          {pillar.grade}
        </span>

      </div>

      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
        {pillar.label}
      </p>

      <p className="mt-1 font-display text-2xl font-extrabold tabular-nums text-[hsl(148,38%,16%)]">

        {pillar.score ??
          "—"}

        <span className="text-sm text-muted-foreground">
          /100
        </span>

      </p>

      {pillar.data_limited && (
        <span className="text-[10px] italic text-muted-foreground">
          indicative
        </span>
      )}

    </button>
  );
}


function PriorityCard({
  recommendation,
  testid,
  navigate,
}) {
  const [
    open,
    setOpen,
  ] =
    useState(
      false
    );

  const config =
    PRIO[
      recommendation.priority
    ] ||
    PRIO.low;

  return (
    <Card
      className={`border-l-4 p-5 ${config.border}`}
      data-testid={
        testid
      }
    >

      <div className="flex flex-wrap items-start justify-between gap-3">

        <div className="flex flex-wrap items-center gap-2">

          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${config.chip}`}
          >
            {config.label}
          </span>

          <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground capitalize">
            {recommendation.pillar.replace(
              "_",
              " "
            )}
          </span>

          {recommendation.group_name && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">

              <Users className="h-3 w-3" />

              {recommendation.group_name}

            </span>
          )}

        </div>

        {recommendation.economic_value_eur_per_day !=
          null && (

          <span className="inline-flex items-center gap-1 text-xs font-semibold tabular-nums text-[hsl(148,38%,16%)]">

            <Euro className="h-3.5 w-3.5 text-primary" />

            {Math.round(
              recommendation.economic_value_eur_per_day
            ).toLocaleString()}

            {recommendation.pillar ===
            "feed_security"
              ? ""
              : "/day"}

          </span>
        )}

      </div>


      <h3 className="mt-3 font-display text-lg font-bold text-[hsl(148,38%,16%)]">
        {recommendation.title}
      </h3>

      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
        {recommendation.summary}
      </p>


      {recommendation.evidence?.length >
        0 && (

        <div className="mt-3 flex flex-wrap gap-2">

          {recommendation.evidence.map(
            (
              evidence,
              index
            ) => (

              <span
                key={
                  index
                }
                className="rounded-full bg-secondary px-2.5 py-1 text-xs text-muted-foreground"
              >
                <span className="font-medium text-[hsl(148,38%,16%)]">
                  {evidence.label}:
                </span>{" "}
                {evidence.value}
              </span>

            )
          )}

        </div>
      )}


      {(recommendation.biological_benefit ||
        recommendation.economic_impact) && (

        <div className="mt-4 grid gap-2 sm:grid-cols-2">

          {recommendation.biological_benefit && (

            <div className="rounded-lg bg-primary/[0.05] p-3 text-xs">

              <span className="inline-flex items-center gap-1 font-semibold text-primary">

                <TrendingUp className="h-3 w-3" />

                Biological benefit

              </span>

              <p className="mt-1 text-muted-foreground">
                {recommendation.biological_benefit}
              </p>

            </div>
          )}

          {recommendation.economic_impact && (

            <div className="rounded-lg bg-secondary/60 p-3 text-xs">

              <span className="inline-flex items-center gap-1 font-semibold text-[hsl(148,38%,16%)]">

                <Euro className="h-3 w-3" />

                Economic impact

              </span>

              <p className="mt-1 text-muted-foreground">
                {recommendation.economic_impact}
              </p>

            </div>
          )}

        </div>
      )}


      <div className="mt-4">

        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">
          Suggested actions
        </p>

        <ul className="space-y-1">

          {(recommendation.actions ||
            []).slice(
            0,
            3
          ).map(
            (
              action,
              index
            ) => (

              <li
                key={
                  index
                }
                className="flex items-start gap-2 text-sm text-[hsl(148,38%,16%)]"
              >

                <ArrowRight className="mt-1 h-3.5 w-3.5 shrink-0 text-primary" />

                {action}

              </li>

            )
          )}

        </ul>

      </div>


      <div className="mt-4 flex flex-wrap items-center gap-2">

        {recommendation.group_name && (

          <Button
            size="sm"
            variant="outline"
            className="rounded-full"
            onClick={() =>
              navigate(
                "/app/nutrition-evaluation"
              )
            }
          >
            Review ration
            <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Button>
        )}

        <Collapsible
          open={
            open
          }
          onOpenChange={
            setOpen
          }
        >

          <CollapsibleTrigger asChild>

            <button
              data-testid={`${testid}-expand`}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
            >

              <BookOpen className="h-3.5 w-3.5" />

              {open
                ? "Hide"
                : "Show"}{" "}
              method & references

              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform ${
                  open
                    ? "rotate-180"
                    : ""
                }`}
              />

            </button>

          </CollapsibleTrigger>

          <CollapsibleContent className="mt-2 rounded-lg border border-dashed border-border p-3">

            <p className="text-xs">

              <span className="font-semibold text-[hsl(148,38%,16%)]">
                Method:
              </span>{" "}

              <span className="text-muted-foreground">
                {recommendation.equation}
              </span>

            </p>

            <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-muted-foreground">

              {(recommendation.references ||
                []).map(
                (
                  reference,
                  index
                ) => (
                  <li
                    key={
                      index
                    }
                  >
                    {reference}
                  </li>
                )
              )}

            </ul>

          </CollapsibleContent>

        </Collapsible>

      </div>

    </Card>
  );
}


function GroupCard({
  group,
  navigate,
}) {
  const order = [
    "energy",
    "protein",
    "fibre",
    "minerals",
  ];

  return (
    <Card
      className="p-5"
      data-testid={`advisor-group-${group.group_id}`}
    >

      <div className="flex items-center gap-3">

        <ScoreRing
          score={
            group.overall
              .score
          }
          size={
            60
          }
        />

        <div className="min-w-0">

          <h3 className="truncate font-display font-bold text-[hsl(148,38%,16%)]">
            {group.group_name}
          </h3>

          <p className="text-xs text-muted-foreground">
            {group.head_count} head ·{" "}
            {group.group_type}
            {group.ration_name
              ? ` · ${group.ration_name}`
              : ""}
          </p>

        </div>

      </div>


      <div className="mt-4 grid grid-cols-2 gap-2">

        {order.map(
          (
            key
          ) => {
            const pillar =
              group
                .pillars[
                key
              ];

            if (!pillar) {
              return null;
            }

            return (
              <div
                key={
                  key
                }
                className="rounded-lg bg-secondary/50 p-2"
              >

                <div className="flex items-center justify-between">

                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {pillar.label}
                  </span>

                  <span
                    className="text-xs font-bold tabular-nums"
                    style={{
                      color:
                        scoreColor(
                          pillar.score
                        ),
                    }}
                  >
                    {pillar.score ??
                      "—"}
                  </span>

                </div>

                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white">

                  <div
                    className="h-full rounded-full"
                    style={{
                      width:
                        `${pillar.score ?? 0}%`,

                      background:
                        scoreColor(
                          pillar.score
                        ),
                    }}
                  />

                </div>

              </div>
            );
          }
        )}

      </div>


      <Button
        variant="outline"
        size="sm"
        className="mt-4 w-full rounded-full"
        onClick={() =>
          navigate(
            "/app/nutrition-evaluation"
          )
        }
      >
        {group.ration_name
          ? "Open ration"
          : "Assign a ration"}

        <ArrowRight className="ml-1.5 h-3.5 w-3.5" />

      </Button>

    </Card>
  );
}


export default function NutritionAdvisor() {
  const {
    farmId,
    farm,
    loading,
  } =
    useFarm();

  const currency =
    farm?.currency ||
    "EUR";

  const currencySymbol =
    getCurrencySymbol(
      currency
    );

  const navigate =
    useNavigate();

  const [
    milkPrice,
    setMilkPrice,
  ] =
    useState(
      0.42
    );

  const [
    pillar,
    setPillar,
  ] =
    useState(
      null
    );

  const [
    question,
    setQuestion,
  ] =
    useState(
      ""
    );


  const {
    data,
  } =
    useQuery({
      queryKey: [
        "advisor",
        farmId,
        milkPrice,
      ],

      queryFn:
        async () =>
          (
            await api.get(
              `/farms/${farmId}/advisor?milk_price=${milkPrice || 0.42}`
            )
          ).data,

      enabled:
        Boolean(
          farmId
        ),

      keepPreviousData:
        true,
    });


  if (
    !loading &&
    !farmId
  ) {
    return (
      <NoFarm />
    );
  }


  if (!data) {
    return (
      <div className="text-muted-foreground">
        Analysing your farm…
      </div>
    );
  }


  const scorecard =
    data.scorecard;

  const pillars =
    Object.values(
      scorecard.pillars
    );


  const recommendations =
    pillar
      ? data.recommendations.filter(
          (
            recommendation
          ) =>
            recommendation.pillar ===
            pillar
        )
      : data.recommendations;


  const priorityRank = {
    critical: 0,
    high: 1,
    medium: 2,
    low: 3,
  };


  const priorityRecommendations =
    [
      ...recommendations,
    ]
      .sort(
        (
          a,
          b
        ) =>
          (
            priorityRank[
              a.priority
            ] ??
            9
          ) -
          (
            priorityRank[
              b.priority
            ] ??
            9
          )
      )
      .slice(
        0,
        4
      );


  const priorityGroups =
    [
      ...(data.groups ||
        []),
    ].sort(
      (
        a,
        b
      ) =>
        (
          a.overall
            ?.score ??
          999
        ) -
        (
          b.overall
            ?.score ??
          999
        )
    );


  const highestPriorityGroup =
    priorityGroups[
      0
    ];


  const issueCount =
    (
      data.counts
        ?.critical ||
      0
    ) +
    (
      data.counts
        ?.high ||
      0
    );


  const opportunityCount =
    (
      data.counts
        ?.medium ||
      0
    ) +
    (
      data.counts
        ?.low ||
      0
    );


  const handleAsk =
    () => {
      const clean =
        question.trim();

      if (!clean) {
        return;
      }

      const lower =
        clean.toLowerCase();

      if (
        lower.includes(
          "ration"
        ) ||
        lower.includes(
          "feed"
        ) ||
        lower.includes(
          "mineral"
        ) ||
        lower.includes(
          "energy"
        ) ||
        lower.includes(
          "protein"
        )
      ) {
        navigate(
          "/app/nutrition-evaluation"
        );
        return;
      }

      if (
        lower.includes(
          "security"
        ) ||
        lower.includes(
          "stock"
        )
      ) {
        navigate(
          "/app/feed-budget"
        );
        return;
      }

      setQuestion(
        ""
      );
    };


  return (
    <div>

      <PageHeader
        title="AI Nutrition Advisor"
        subtitle="Your farm nutrition copilot — using your rations, feed stocks, grass data and animal groups."
        action={
          <div data-testid="milk-price-control">

            <Label className="text-xs">
              Milk price ({currencySymbol}/L)
            </Label>

            <Input
              type="number"
              min="0"
              step="0.01"
              value={
                milkPrice
              }
              data-testid="milk-price-input"
              onChange={(
                event
              ) =>
                setMilkPrice(
                  event.target
                    .value ===
                  ""
                    ? ""
                    : Math.max(
                        0,
                        Number(
                          event.target
                            .value
                        )
                      )
                )
              }
              className="mt-1 w-28 text-right"
            />

          </div>
        }
      />


      {/* ------------------------------------------------ */}
      {/* Farm nutrition summary                          */}
      {/* ------------------------------------------------ */}

      <Card
        className="mb-6 p-5 sm:p-6"
        data-testid="advisor-scorecard"
      >

        <div className="grid gap-6 xl:grid-cols-[auto_1fr] xl:items-center">

          <div className="flex items-center gap-4">

            <ScoreRing
              score={
                scorecard
                  .overall
                  .score
              }
              size={
                96
              }
            />

            <div>

              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                Farm nutrition status
              </p>

              <p className="mt-1 font-display text-xl font-bold text-[hsl(148,38%,16%)]">
                Grade{" "}
                {scorecard
                  .overall
                  .grade}
              </p>

              <div className="mt-3 space-y-1 text-xs text-muted-foreground">

                <p>
                  <strong className="text-foreground">
                    {issueCount}
                  </strong>{" "}
                  issue
                  {issueCount ===
                  1
                    ? ""
                    : "s"}{" "}
                  need attention
                </p>

                <p>
                  <strong className="text-foreground">
                    {opportunityCount}
                  </strong>{" "}
                  opportunity
                  {opportunityCount ===
                  1
                    ? ""
                    : "ies"}{" "}
                  identified
                </p>

                {highestPriorityGroup && (
                  <p>
                    Highest-priority group:{" "}
                    <strong className="text-foreground">
                      {highestPriorityGroup.group_name}
                    </strong>
                  </p>
                )}

              </div>

            </div>

          </div>


          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">

            {pillars.map(
              (
                item
              ) => (

                <PillarTile
                  key={
                    item.key
                  }
                  pillar={
                    item
                  }
                  active={
                    pillar ===
                    item.key
                  }
                  onClick={() =>
                    setPillar(
                      pillar ===
                        item.key
                        ? null
                        : item.key
                    )
                  }
                />

              )
            )}

          </div>

        </div>

      </Card>


      {/* ------------------------------------------------ */}
      {/* Ask Farm Hand                                   */}
      {/* ------------------------------------------------ */}

      <Card className="mb-7 border-primary/20 bg-primary/[0.025] p-5 sm:p-6">

        <div className="flex items-start gap-3">

          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary/10">

            <Sparkles className="h-5 w-5 text-primary" />

          </div>

          <div className="flex-1">

            <h2 className="font-display text-lg font-bold text-[hsl(148,38%,16%)]">
              Ask Farm Hand
            </h2>

            <p className="mt-1 text-sm text-muted-foreground">
              Ask about your feeds, rations, feed security or nutrition results.
            </p>

          </div>

        </div>


        <div className="mt-5 flex flex-col gap-2 sm:flex-row">

          <Input
            value={
              question
            }
            onChange={(
              event
            ) =>
              setQuestion(
                event.target
                  .value
              )
            }
            onKeyDown={(
              event
            ) => {
              if (
                event.key ===
                "Enter"
              ) {
                handleAsk();
              }
            }}
            placeholder="Ask about your farm, feeds or rations..."
            className="h-11"
          />

          <Button
            className="h-11 rounded-full px-5"
            onClick={
              handleAsk
            }
          >
            <Send className="mr-2 h-4 w-4" />
            Ask Farm Hand
          </Button>

        </div>


        <div className="mt-3 flex flex-wrap gap-2">

          {QUICK_PROMPTS.map(
            (
              prompt
            ) => (

              <button
                key={
                  prompt
                }
                type="button"
                onClick={() => {
                  setQuestion(
                    prompt
                  );
                }}
                className="rounded-full border bg-background px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:border-primary/40 hover:text-primary"
              >
                {prompt}
              </button>

            )
          )}

        </div>


        <p className="mt-3 text-[11px] text-muted-foreground">
          Farm Hand uses the existing nutrition and feed engines for calculations. The assistant layer is for explanation, prioritisation and navigation.
        </p>

      </Card>


      {/* ------------------------------------------------ */}
      {/* Priority actions                                 */}
      {/* ------------------------------------------------ */}

      <div className="mb-3 flex items-center justify-between gap-3">

        <h2 className="flex items-center gap-2 font-display text-lg font-bold text-[hsl(148,38%,16%)]">

          <Lightbulb className="h-5 w-5 text-primary" />

          Priority actions
          {pillar
            ? ` · ${scorecard.pillars[pillar].label}`
            : ""}

        </h2>

        {pillar && (

          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              setPillar(
                null
              )
            }
            data-testid="clear-pillar-filter"
          >
            Show all
          </Button>
        )}

      </div>


      <div
        className="grid grid-cols-1 gap-4 xl:grid-cols-2"
        data-testid="advisor-recommendations"
      >

        {priorityRecommendations.map(
          (
            recommendation
          ) => (

            <PriorityCard
              key={
                recommendation.id
              }
              recommendation={
                recommendation
              }
              testid={`rec-${recommendation.id}`}
              navigate={
                navigate
              }
            />

          )
        )}

        {priorityRecommendations.length ===
          0 && (

          <Card className="p-8 text-center">

            <CheckCircle2Fallback />

            <p className="mt-2 text-sm font-semibold text-[hsl(148,38%,16%)]">
              No recommendations in this area.
            </p>

            <p className="mt-1 text-xs text-muted-foreground">
              Current data does not indicate an issue here.
            </p>

          </Card>
        )}

      </div>


      {/* ------------------------------------------------ */}
      {/* Group overview                                   */}
      {/* ------------------------------------------------ */}

      <h2 className="mb-3 mt-8 flex items-center gap-2 font-display text-lg font-bold text-[hsl(148,38%,16%)]">

        <Users className="h-5 w-5 text-primary" />

        Animal group overview

      </h2>


      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">

        {priorityGroups.map(
          (
            group
          ) => (

            <GroupCard
              key={
                group.group_id
              }
              group={
                group
              }
              navigate={
                navigate
              }
            />

          )
        )}

      </div>


      <div className="mt-6 rounded-xl border border-dashed p-4">

        <p className="text-xs leading-relaxed text-muted-foreground">
          The Advisor interprets existing Farm Hand engine outputs rather than creating new nutrient equations. Economic estimates use current feed costs and a milk price of{" "}
          {currencySymbol}
          {Number(
            milkPrice ||
              0.42
          ).toFixed(
            2
          )}
          /L.
        </p>

      </div>

    </div>
  );
}


/*
 * Small local icon fallback used only for the empty-state card.
 * Kept here to avoid adding another dependency/import.
 */
function CheckCircle2Fallback() {
  return (
    <div className="mx-auto grid h-9 w-9 place-items-center rounded-full bg-primary/10 text-primary">
      <span className="text-lg font-bold">
        ✓
      </span>
    </div>
  );
}
